import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../features/helpers";
import { fetchLastestTransactions } from "../../store/TransactionSlice";
import { checkDirectionClass, transactionDirection, TransactionStatus, transactionType } from "../../utils/direction-format";
import { formatCurrencyBRL } from "../../utils/format";
import SkeletonLine from "../skeleton/SkeletonLine";
import "./orgEntries.scss";
import { FunctionComponent, useEffect, useState } from "react";
import { userInfo } from "os";
import { fetchPurchasesByOrganizationToday } from "../../store/PurchaseSlice";
import { LatestResponse } from "../../models/Receipts";
import { fetchLatest } from "../../store/ReceiptsSlice";

const MAX_COUNT = 10;

const OrgEntries : FunctionComponent = () => {

    const dispatch = useAppDispatch()

    const invoices          = useAppSelector( (state) => state.receipts.latestInvoices )
    const transactions      = useAppSelector( (state) => state.receipts.latestTransactions )
    const purchases         = useAppSelector( (state) => state.receipts.latestPurchases )
    const deposits          = useAppSelector( (state) => state.receipts.latestDeposits ) 

    const [data, setData] = useState<LatestResponse | null>(null);
    const [type, setType] = useState<string>('all');

    useEffect( () => {
        if (type === 'all') setData(transactions ?? null);
    }, [transactions])

    useEffect( () => {
        if (type === 'invoices') setData(invoices ?? null);
    }, [invoices])

    useEffect( () => {
        if (type === 'purchases') setData(purchases ?? null);
    }, [purchases])

    useEffect( () => {
        if (type === 'deposits' && deposits) {
            const arr = deposits.data.map((deposito: any) => ({
                ...deposito,
                value: deposito.d_value,
                status: deposito.d_status,
                updatedAt: deposito.d_updatedAt
            }));
            setData({
                data: arr,
                total: deposits.total,
                pagina: deposits.pagina,
                hasMore: deposits.hasMore,
            });
        }
    }, [deposits])

    const changeTab = (type:string) => {
        setType(type);

        if (type === 'all') {
            setData(transactions ?? null);
        }
        if (type === 'deposits') {
            setData(deposits ?? null);
        }
        if (type === 'invoices') {
            setData(invoices ?? null);
        }
        if (type === 'purchases') {
            setData(purchases ?? null);
        }
        
    }

    const loadMore = (page:number) => {
        dispatch( fetchLatest(type, page));
    } 

    return (
        <div className="list-entries">

            { !data && <SkeletonLine height="40" />}
            { data &&
                <div className="table">
                    
                    <div className="header">
                        <h1>
                            Ultimas movimentacoes
                            <small>{ data.data.length > 10 ? MAX_COUNT : data.data.length} entradas</small>
                        </h1>

                        <div className="list-wrapper">
                            <div className={`li-shadow ${type}`}></div>
                            <ul>
                                <li onClick={() => changeTab('all')} className={`${type === 'all' ? 'active' : ''}`}>Todos</li>
                                <li onClick={() => changeTab('deposits')} className={`${type === 'deposits' ? 'active' : ''}`}>Depositos</li>
                                <li onClick={() => changeTab('invoices')} className={`${type === 'invoices' ? 'active' : ''}`}>Saques</li>
                                <li onClick={() => changeTab('purchases')} className={`${type === 'purchases' ? 'active' : ''}`}>Compras</li>
                            </ul>
                        </div>
                    </div>
                    
                    <div className="custom-table-body">
                        <div className="body-header grid-custom">
                            <span>Descricao</span>
                            <span>Valor</span>
                            <span>Data</span>
                            <span>Status</span>
                        </div>
                        { data && data.data.length  > 0 && data.data.map( (entry, index) => (
                            <>
                                <div className={checkDirectionClass(entry, 'item grid-custom')} key={index}>
                                    <div className='entry-info'>

                                        { type === 'all' && (
                                            <>
                                                <div className={transactionDirection(entry, 'rounded')}>
                                                    { entry.direction === 'IN' && <ArrowUpRight size={24} />}
                                                    { entry.direction === 'OUT' && <ArrowDownRight size={24} />}
                                                    { entry.direction !== 'OUT' && entry.direction !== 'IN' && <Minus size={24} />}
                                                </div>
                                                <div className="info">
                                                    <div className="columns">
                                                        <i>{ transactionType(type) } -  {entry.type} </i>
                                                        <b>
                                                            { entry.direction ?
                                                                <>
                                                                    { entry.direction === 'OUT' && 'Saida' }
                                                                    { entry.direction === 'IN' && 'Entrada' }
                                                                </> 
                                                                :
                                                                <>
                                                                    oi
                                                                </>
                                                            }
                                                        </b>
                                                    </div>
                                                    <div className="columns">
                                                        <p>
                                                            { entry.purchases && entry.purchases.length > 0 && <span>Compra: {entry.purchases[0].target_user.name}</span>}
                                                            { entry.payments && entry.payments.length > 0 && <span>Pagamento: {entry.payments[0].target.name}</span>}
                                                            { entry.deposits && entry.deposits.length > 0 && <span>Deposito: {entry.deposits[0].target.name}</span>}
                                                            { entry.invoices && entry.invoices.length > 0 && <span>Envio</span>}
                                                        </p>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        { type === 'deposits' && (
                                            <>
                                                <div className={checkDirectionClass(entry, 'rounded')}>
                                                    { Number(entry.value) > 0 && <ArrowUpRight size={24} />}
                                                    { Number(entry.value) < 0 && <ArrowDownRight size={24} />}
                                                </div>
                                                <div className="info">
                                                    <i>{ transactionType(type) } -  {entry.d_type} </i>
                                                    <b>Saida</b>
                                                </div>
                                            </>
                                        )}
                                        { type === 'invoices' && (
                                            <>
                                                <div className={checkDirectionClass(entry, 'rounded', true)}>
                                                    { Number(entry.value) < 0 && <ArrowUpRight size={24} />}
                                                    { Number(entry.value) > 0 && <ArrowDownRight size={24} />}
                                                </div>
                                                <div className="info">
                                                    <i>{ transactionType(type) } -  {entry.asset.name} </i>
                                                    <b>Saida</b>
                                                </div>
                                            </>
                                        )}
                                        { type === 'purchases' && (
                                            <>
                                                
                                                <div className={checkDirectionClass(entry, 'rounded')}>
                                                    { Number(entry.value) > 0 && <ArrowUpRight size={24} />}
                                                    { Number(entry.value) < 0 && <ArrowDownRight size={24} />}
                                                </div>
                                                <div className="info">
                                                    <i>{ transactionType(type) } -  {entry.d_type} </i>
                                                    <b>Saida</b>
                                                </div>
                                            </>
                                        )}

                                    </div>
                                    <span className="value">
                                        {formatCurrencyBRL(Number(entry.value))}
                                    </span>
                                    <span>
                                        {entry.updatedAt}
                                    </span>
                                    <span className={TransactionStatus(entry)}>
                                        {entry.status}
                                    </span>
                                </div>
                            </>
                        ))}
                        { data.hasMore && (
                            <div className="buttons">
                                <button className="btn purple-neon" onClick={ () => loadMore(data.pagina+1) }>
                                    Carregar Mais
                                </button>
                            </div>
                        )}

                        { data && data.data.length === 0 && <p className="flex-item center center-vertical">Sem nenhuma entrada</p>}
                    </div>

                </div>
            }

        </div>
    )
}

export default OrgEntries;